import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useRef } from 'react';
import { z } from 'zod';
import client from '../client';
import { isErrorEnvelope, parseEnvelope } from '../envelope';

const uuidSchema = z.string().uuid();
const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must use YYYY-MM-DD');
const displayTextSchema = z.string().min(1).max(200);
const safeTextSchema = z.string().min(1).max(160);
const outcomeSchema = z.enum(['event', 'condition', 'event_and_condition']);
const eventInputSchema = z.object({
  name: displayTextSchema,
  kind: z.enum(['trip', 'project', 'other']),
  start_date: dateSchema,
  end_date: dateSchema,
  location: displayTextSchema.nullable(),
  tag_business: z.boolean(),
}).strict();
const conditionInputSchema = z.object({ name: displayTextSchema, enabled: z.boolean() }).strict();

export const askCommitInputSchema = z.object({
  draft_id: uuidSchema,
  operation_id: uuidSchema,
  outcome: outcomeSchema,
  accept_ids: z.array(uuidSchema).max(200),
  event: eventInputSchema.nullable().optional(),
  condition: conditionInputSchema.nullable().optional(),
  acknowledge_partial_source: z.boolean(),
}).strict().superRefine((input, context) => {
  if (new Set(input.accept_ids).size !== input.accept_ids.length) {
    context.addIssue({ code: 'custom', path: ['accept_ids'], message: 'accept_ids must be unique' });
  }
  if (input.outcome === 'event' && (!input.event || input.accept_ids.length === 0)) {
    context.addIssue({ code: 'custom', path: ['outcome'], message: 'event outcome requires accepted IDs and event' });
  }
  if (input.outcome === 'condition' && !input.condition) {
    context.addIssue({ code: 'custom', path: ['outcome'], message: 'condition outcome requires condition' });
  }
  if (input.outcome === 'event_and_condition' && (!input.event || !input.condition || input.accept_ids.length === 0)) {
    context.addIssue({ code: 'custom', path: ['outcome'], message: 'combined outcome requires accepted IDs, event, and condition' });
  }
});
export type AskCommitInput = z.infer<typeof askCommitInputSchema>;

const eventSummarySchema = z.object({
  event_id: uuidSchema,
  version: z.number().int().positive(),
  name: displayTextSchema,
  kind: z.enum(['trip', 'project', 'other']),
  start_date: dateSchema,
  end_date: dateSchema,
  location: displayTextSchema.nullable(),
  is_business: z.boolean(),
}).strict();
const conditionSummarySchema = z.object({
  rule_id: uuidSchema,
  version: z.number().int().positive(),
  name: displayTextSchema,
  enabled: z.boolean(),
  cache_state: z.enum(['current', 'rebuilding', 'failed']),
}).strict();

export const askCommitResponseSchema = z.object({
  operation_id: uuidSchema,
  idempotent_replay: z.boolean(),
  draft_id: uuidSchema,
  event: eventSummarySchema.nullable(),
  condition: conditionSummarySchema.nullable(),
  applied_count: z.number().int().nonnegative(),
  unchanged_count: z.number().int().nonnegative(),
  accepted_ids: z.array(uuidSchema).max(200),
  annotation_batch_id: uuidSchema.nullable(),
  domain_audits: z.array(z.object({ target_kind: z.enum(['event', 'rule', 'annotation']), target_id: uuidSchema, action: safeTextSchema }).strict()).max(200),
  cache_impacts: z.array(z.enum(['transactions', 'events', 'rules', 'rule_matches', 'jobs'])).max(50),
  navigation_target: z.string().max(1024).nullable(),
  message: safeTextSchema.nullable(),
}).strict().superRefine((result, context) => {
  if (new Set(result.accepted_ids).size !== result.accepted_ids.length) {
    context.addIssue({ code: 'custom', path: ['accepted_ids'], message: 'accepted_ids must be unique' });
  }
  if (new Set(result.cache_impacts).size !== result.cache_impacts.length) {
    context.addIssue({ code: 'custom', path: ['cache_impacts'], message: 'cache_impacts must be unique' });
  }
});
export type AskCommitResult = z.infer<typeof askCommitResponseSchema>;

export class AskCommitConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AskCommitConflictError';
  }
}

export class AskCommitExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AskCommitExpiredError';
  }
}

export class AskCommitUnknownOutcomeError extends Error {
  constructor() {
    super('We could not confirm whether this Ask request completed.');
    this.name = 'AskCommitUnknownOutcomeError';
  }
}

function bodyFromInput(input: AskCommitInput) {
  const parsed = askCommitInputSchema.parse(input);
  return {
    operation_id: parsed.operation_id,
    outcome: parsed.outcome,
    accept_ids: parsed.accept_ids,
    ...(parsed.event === undefined ? {} : { event: parsed.event }),
    ...(parsed.condition === undefined ? {} : { condition: parsed.condition }),
    acknowledge_partial_source: parsed.acknowledge_partial_source,
  };
}

function classifyCommitError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    if (!error.response) throw new AskCommitUnknownOutcomeError();
    if (isErrorEnvelope(error.response.data)) {
      const { code, message } = error.response.data.error;
      if (code === 'changed_since') throw new AskCommitConflictError(message);
      if (code === 'draft_expired' || code === 'expired') throw new AskCommitExpiredError(message);
    }
  }
  throw error;
}

export function useAskCommit() {
  const queryClient = useQueryClient();
  const lastInputRef = useRef<AskCommitInput | null>(null);
  const mutation = useMutation<AskCommitResult, Error, AskCommitInput>({
    mutationFn: async (input) => {
      const parsed = askCommitInputSchema.parse(input);
      lastInputRef.current = parsed;
      try {
        const response = await client.post(`/api/ask/${parsed.draft_id}/commit`, bodyFromInput(parsed));
        return parseEnvelope(askCommitResponseSchema, response.data).data;
      } catch (error) {
        classifyCommitError(error);
      }
    },
    retry: false,
    onSuccess: (result) => {
      for (const impact of result.cache_impacts) {
        if (impact === 'transactions') queryClient.invalidateQueries({ queryKey: ['transactions'] });
        if (impact === 'events') queryClient.invalidateQueries({ queryKey: ['events'] });
        if (impact === 'rules' || impact === 'rule_matches') queryClient.invalidateQueries({ queryKey: ['rules'] });
        if (impact === 'jobs') queryClient.invalidateQueries({ queryKey: ['jobs'] });
      }
      queryClient.invalidateQueries({ queryKey: ['ask', result.draft_id] });
    },
  });

  return {
    ...mutation,
    replay: () => {
      if (lastInputRef.current) mutation.mutate(lastInputRef.current);
    },
    get operationId() {
      return lastInputRef.current?.operation_id;
    },
  };
}
